/**
 * Settings Modal - Main orchestration file
 * Handles modal creation, state management, and event handling
 */

import { HangoutState } from '../../state/HangoutState.js';
import { HangoutService } from '../../services/HangoutService.js';
import { getCurrentLanguage, loadTranslations, applyTranslationsToContainer } from '../../../translations.js';
import { getOutfitId } from '../../../utils/characterUtils.js';
import { normalizeGender } from '../../../utils/genderUtils.js';
import { normalizeSiteLanguageCode } from '../../../utils/languageData.js';
import { storageManager } from '../../../../core/storage/index.js';
import { storage } from '../../../utils/storage.js';
import { openModal, closeModal } from '../modals/modal-utils.js';
import { createCharacterTabs, renderCharacterInfoContent, createVibeSelector, refreshSpriteGridForCurrentOutfit } from './character-info.js';
import {
    applySettingsLocationAndTimeToSelects,
    getCurrentStateFromMessages,
    resolveSettingsLocationAndTime
} from './settings-state.js';
import { handleSettingsSubmit, updateValidationMessage } from './settings-submit-handler.js';
import { initSearchDropdown } from '../../../create/ui/searchDropdown.js';
import { attachRankedCharacterPickerToTrigger } from './character-picker-modal.js';
import { attachCreateLocationPickerToTrigger } from '../../../create/ui/locationPickerModal.js';
import { GROK_WORKER_URL } from '../../../config.js';
import { fetchCache } from '../../../utils/fetchCache.js';
import { fetchOpenRouterKeyStatus } from '../../../utils/openRouterKeyStatus.js';
import { showToast } from '../../../utils/toast.js';
import { showConfirm } from '../../../utils/UnifiedNotificationSystem.js';
import { interpolate } from '../../../translations.js';
import { sortCharactersByRankAsc } from '../../../create/utils/mapCreateCharacters.js';
import {
    CHAT_SUMMARY_MAX_LENGTH,
    getChatMemory,
    getPendingSummaryStats,
    getSummarizableHistory,
    setChatMemory
} from '../hangout-memory-storage.js';
import { schedulePersistChatSummary } from '../hangout-settings-persist.js';
import { estimateTokens, formatTokenCount } from '../token-estimate.js';

// Extracted utilities
import { preloadAllOutfits, loadOutfits, fetchCharacterOutfitIds, isOutfitUnavailableForCharacter } from './settings-outfit-utils.js';
import { getUserData, isOutfitAvailable, loadOutfitExceptionKeys } from './settings-user-utils.js';
import { 
    toIdString, 
    getConfiguredActiveCharacters, 
    getActiveCharacterForSlot, 
    getEnhancedCharacterData,
    CREW_MAP 
} from './settings-character-utils.js';
import { 
    createElement, 
    updatePreview, 
    setPreviewLocked,
    fetchSprite,
    getSettingsModalTranslationText
} from './settings-dom-utils.js';
import { 
    createCharacterSection, 
    PreviewStateManager, 
    updateCharacterSectionsVisibility 
} from './character-section-builder.js';
import { createBackgroundSection, loadBackgroundOptions, refreshSettingsBackgroundPreview } from './background-section-builder.js';

let currentSettingsModal = null;
let lastSettingsTab = 'scenario';

const resetSettingsModalScroll = (modal) => {
    if (!modal) return;
    const reset = () => {
        modal.scrollTop = 0;
        const content = modal.querySelector('.modal-content');
        if (content) content.scrollTop = 0;
        const views = modal.querySelector('.settings-unified-views');
        if (views) views.scrollTop = 0;
    };
    requestAnimationFrame(() => {
        reset();
        requestAnimationFrame(reset);
    });
};

const normalizeDisplayLanguage = (language) => normalizeSiteLanguageCode(language);

const resolveSettingsLanguage = () => {
    const fromTranslations = getCurrentLanguage();
    const fromStorage = storage.getLanguage();
    return normalizeDisplayLanguage(fromStorage || fromTranslations || 'en');
};

const getSettingsModalText = getSettingsModalTranslationText;

const readScenarioFormSnapshot = () => {
    const read = (id) => document.getElementById(id)?.value ?? '';
    return {
        description: read('scenario-description-settings'),
        numCharacters: read('num-allCharacters-settings'),
        backgroundId: read('generate-bg-settings'),
        timeOfDay: read('time-of-day-settings'),
        slots: CREW_MAP.map(({ charSelect, roleSelect }, index) => ({
            characterId: read(charSelect),
            outfitId: read(roleSelect),
            goal: read(`char-${index + 1}-goal-settings`)
        }))
    };
};

const scenarioSnapshotsEqual = (left, right) => JSON.stringify(left) === JSON.stringify(right);

const waitForOutfitOptions = async (snapshot, timeoutMs = 2500) => {
    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
        const ready = snapshot.slots.every((slot, index) => {
            if (!slot.outfitId) return true;
            const roleEl = document.getElementById(CREW_MAP[index].roleSelect);
            if (!roleEl) return true;
            return Array.from(roleEl.options).some((option) => option.value === String(slot.outfitId));
        });
        if (ready) return;
        await new Promise((resolve) => setTimeout(resolve, 40));
    }
};

const applyScenarioSnapshotValues = async (snapshot) => {
    if (!snapshot) return;

    const descriptionEl = document.getElementById('scenario-description-settings');
    if (descriptionEl) {
        descriptionEl.value = snapshot.description;
        descriptionEl.dispatchEvent(new Event('input', { bubbles: true }));
    }

    const numEl = document.getElementById('num-allCharacters-settings');
    if (numEl && numEl.value !== snapshot.numCharacters) {
        numEl.value = snapshot.numCharacters;
        numEl.dispatchEvent(new Event('change', { bubbles: true }));
    }

    snapshot.slots.forEach((slot, index) => {
        const { charSelect, roleSelect } = CREW_MAP[index];
        const charEl = document.getElementById(charSelect);
        const roleEl = document.getElementById(roleSelect);
        const goalEl = document.getElementById(`char-${index + 1}-goal-settings`);
        if (goalEl) goalEl.value = slot.goal;
        if (roleEl) roleEl.dataset.preferredOutfitId = slot.outfitId || '';
        if (charEl) {
            charEl.value = slot.characterId;
            charEl.dispatchEvent(new Event('change', { bubbles: true }));
        }
    });

    await waitForOutfitOptions(snapshot);

    snapshot.slots.forEach((slot, index) => {
        const roleEl = document.getElementById(CREW_MAP[index].roleSelect);
        if (!roleEl || roleEl.value === slot.outfitId) return;
        roleEl.value = slot.outfitId;
        roleEl.dispatchEvent(new Event('change', { bubbles: true }));
    });

    const backgroundEl = document.getElementById('generate-bg-settings');
    if (backgroundEl && backgroundEl.value !== snapshot.backgroundId) {
        backgroundEl.value = snapshot.backgroundId;
        backgroundEl.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const timeEl = document.getElementById('time-of-day-settings');
    if (timeEl && timeEl.value !== snapshot.timeOfDay) {
        timeEl.value = snapshot.timeOfDay;
        timeEl.dispatchEvent(new Event('change', { bubbles: true }));
    }

    await new Promise((resolve) => setTimeout(resolve, 80));
};

export async function getSettingsModalInstance() {
    if (!currentSettingsModal) {
        currentSettingsModal = await createSettingsModal();
    }
    return currentSettingsModal;
}

export async function refreshSettingsModalContent() {
    if (currentSettingsModal && currentSettingsModal.classList.contains('is-open')) {
        try {
            currentSettingsModal.classList.remove('is-open', 'show');
            await createSettingsModal();
            const newModal = document.getElementById('settings-modal');
            if (newModal) newModal.classList.add('is-open', 'show');
        } catch (error) {
            console.error('Settings modal: Error refreshing modal content:', error);
        }
    }
}

export async function openSettingsModal() {
    try {
        // Load settings modal CSS dynamically
        if (!document.querySelector('link[href="/css/create.css"]')) {
            const { loadSettingsModalCSS } = await import('../../../../src/utils/css-loader.js');
            await loadSettingsModalCSS();
        }

        const existing = document.getElementById('settings-modal');
        const shouldRecreate = await shouldRecreateSettingsModal();

        if (!existing || shouldRecreate) {
            if (existing && shouldRecreate) {
                existing.remove();
                currentSettingsModal = null;
            }
            await createSettingsModal();
        } else {
            await refreshCharacterDropdowns({ shouldDispatchChangeEvents: false });
        }
        const modalForHydrate = document.getElementById('settings-modal');
        if (typeof (/** @type {any} */ (modalForHydrate))?._beginScenarioFormHydrate === 'function') {
            (/** @type {any} */ (modalForHydrate))._beginScenarioFormHydrate();
        }
        await hydrateSettingsModalSelections();
        await refreshOutfitDropdowns();
        if (typeof (/** @type {any} */ (modalForHydrate))?._commitScenarioBaseline === 'function') {
            (/** @type {any} */ (modalForHydrate))._commitScenarioBaseline();
        }
        const modal = document.getElementById('settings-modal');
        if (modal) {
            if (typeof (/** @type {any} */ (modal))._syncCharacterTabsFromConfig === 'function') {
                await (/** @type {any} */ (modal))._syncCharacterTabsFromConfig();
            }
            if (typeof (/** @type {any} */ (modal))._restoreLastTab === 'function') {
                await (/** @type {any} */ (modal))._restoreLastTab();
            } else if (typeof (/** @type {any} */ (modal))._showScenarioTab === 'function') {
                (/** @type {any} */ (modal))._showScenarioTab();
            } else if (typeof (/** @type {any} */ (modal))._showSettingsTab === 'function') {
                (/** @type {any} */ (modal))._showSettingsTab();
            }
            openModal('settings-modal');
            resetSettingsModalScroll(modal);
        }
    } catch (error) {
        console.error('Settings modal: Error opening modal:', error);
    }
}

// Re-translate settings modal when language changes
if (typeof window !== 'undefined' && !window._settingsLangListenerAdded) {
    window._settingsLangListenerAdded = true;
    document.addEventListener('languageSwitched', async () => {
        try {
            await refreshSettingsModalContent();
        } catch (e) {
            console.warn('Settings modal: Failed to refresh on language change', e);
        }
    });
}

async function shouldRecreateSettingsModal() {
    const currentLanguage = toIdString(resolveSettingsLanguage());
    const existing = document.getElementById('settings-modal');
    if (existing) {
        const modalLanguage = toIdString(existing.dataset.language || '');
        if (modalLanguage && modalLanguage !== currentLanguage) {
            return true;
        }
    }

    const hangoutConfig = await storageManager.getItem('currentHangoutConfig') || {};
    if (!hangoutConfig?.allCharacters) return false;

    const hasIncompleteCharacterData = hangoutConfig.allCharacters.some(char =>
        !char.name || char.name === 'Character'
    );

    if (hasIncompleteCharacterData) return true;

    if (existing) {
        const firstCharSelect = existing.querySelector('#crew-char-1-settings');
        if (firstCharSelect && !firstCharSelect.value) return true;
    }
    return false;
}

const getScenarioDescriptionDefault = (scenario) =>
    (scenario?.description || scenario?.descr || '').trim();

const getScenarioGoalForSlot = (scenario, slotIndex, characterId = null) => {
    if (!scenario || typeof slotIndex !== 'number' || slotIndex < 0) return '';

    const goalBySlotKey = scenario[`goal_${slotIndex + 1}`];
    if (typeof goalBySlotKey === 'string' && goalBySlotKey.trim()) {
        return goalBySlotKey.trim();
    }

    const scenarioCharacters = Array.isArray(scenario.chars)
        ? scenario.chars
        : (Array.isArray(scenario.allCharacters) ? scenario.allCharacters : []);

    if (scenarioCharacters.length > 0) {
        if (characterId !== null && characterId !== undefined) {
            const normalizedCharacterId = toIdString(characterId);
            const matched = scenarioCharacters.find(char => toIdString(char?.id) === normalizedCharacterId);
            if (typeof matched?.goal === 'string' && matched.goal.trim()) {
                return matched.goal.trim();
            }
        }

        const slotCharacterGoal = scenarioCharacters[slotIndex]?.goal;
        if (typeof slotCharacterGoal === 'string' && slotCharacterGoal.trim()) {
            return slotCharacterGoal.trim();
        }
    }

    if (slotIndex === 0 && typeof scenario?.goal === 'string' && scenario.goal.trim()) {
        return scenario.goal.trim();
    }

    return '';
};

const resolveScenarioDescriptionForModal = ({
    currentState,
    hangoutState,
    scenario
}) => {
    const defaultScenarioDescription = getScenarioDescriptionDefault(scenario);
    if (defaultScenarioDescription) return defaultScenarioDescription;

    const liveCustomDescription = toIdString(hangoutState?.getCustomScenarioDescription?.() || '').trim();
    if (liveCustomDescription) return liveCustomDescription;

    const stateCustomDescription = toIdString(currentState?.customScenarioDescription || '').trim();
    return stateCustomDescription;
};

async function refreshCharacterDropdowns({ shouldDispatchChangeEvents = true } = {}) {
    try {
        const hangoutConfig = await storageManager.getItem('currentHangoutConfig') || {};

        if (!hangoutConfig?.allCharacters) return;

        const hangoutService = HangoutService.getInstance();
        let allAvailableCharacters = sortCharactersByRankAsc(await hangoutService.getAvailableCharacters());

        const crewMap = CREW_MAP;

        crewMap.forEach(({ charSelect }, index) => {
            const charSelectEl = document.getElementById(charSelect);
            if (!charSelectEl) return;

            const currentValue = charSelectEl.value;
            const selectCharText = charSelectEl.options[0]?.textContent || 'Select a character';
            
            charSelectEl.innerHTML = `<option value="">${selectCharText}</option>`;
            allAvailableCharacters.forEach(char => {
                const option = document.createElement('option');
                option.value = char.id;
                option.textContent = char.name;
                charSelectEl.appendChild(option);
            });

            if (currentValue && charSelectEl.querySelector(`option[value="${currentValue}"]`)) {
                charSelectEl.value = currentValue;
            } else if (index < hangoutConfig.allCharacters.length) {
                const configChar = hangoutConfig.allCharacters[index];
                if (configChar?.id && charSelectEl.querySelector(`option[value="${configChar.id}"]`)) {
                    charSelectEl.value = configChar.id;
                }
            }
        });

        hangoutConfig.allCharacters.forEach((char, index) => {
            const goalTextarea = document.getElementById(`char-${index + 1}-goal-settings`);
            if (goalTextarea && char.goal) {
                goalTextarea.value = char.goal;
            }
        });

        if (shouldDispatchChangeEvents) {
            crewMap.forEach(({ charSelect }) => {
                const el = document.getElementById(charSelect);
                if (el && el.value) {
                    el.dispatchEvent(new Event('change'));
                }
            });
        }

    } catch (error) {
        console.error('Settings Modal: Error refreshing character dropdowns:', error);
    }
}

const MISSING_OUTFIT_CONFIRM_Z_INDEX = 12000;

const appendOutfitSelectOptions = (roleSelectEl, characterOutfits, {
    membershipTier,
    scenarioId,
    characterId,
    exceptionKeys,
    characterOutfitIds
}) => {
    const notAvailableText = getSettingsModalText(
        'outfit_not_available',
        'This outfit is not available for this character'
    );

    characterOutfits.forEach(outfit => {
        const option = createElement('option', '', { value: outfit.id, textContent: outfit.name });
        if (!isOutfitAvailable(outfit, membershipTier, {
            scenarioId,
            characterId,
            exceptionKeys
        })) {
            option.setAttribute('data-unowned', 'true');
        }
        if (isOutfitUnavailableForCharacter(outfit.id, characterOutfitIds)) {
            option.setAttribute('data-unavailable', 'true');
            option.title = notAvailableText;
        }
        roleSelectEl.appendChild(option);
    });
};

const pickSelectableOutfitId = (characterOutfits, preferredOutfitId, roleSelectEl, {
    membershipTier,
    scenarioId,
    characterId,
    exceptionKeys,
    characterOutfitIds
}) => {
    const preferredOption = preferredOutfitId
        ? roleSelectEl.querySelector(`option[value="${preferredOutfitId}"]`)
        : null;
    if (preferredOption && !preferredOption.hasAttribute('data-unavailable')) {
        return preferredOutfitId;
    }

    const availableOutfit = characterOutfits.find(outfit =>
        isOutfitAvailable(outfit, membershipTier, {
            scenarioId,
            characterId,
            exceptionKeys
        }) && !isOutfitUnavailableForCharacter(outfit.id, characterOutfitIds)
    );
    if (availableOutfit) return toIdString(availableOutfit.id);
    return '';
};

const handleMissingOutfitOption = async (option, optionBtn, slotIndex, crewMap) => {
    const charSelect = document.getElementById(crewMap[slotIndex].charSelect);
    const charId = charSelect?.value;
    const charName = charSelect?.selectedOptions?.[0]?.textContent?.trim() || 'Character';
    const outfitName = option?.label || '';
    const outfitId = option?.value;
    const message = interpolate(
        getSettingsModalText(
            'missing_outfit_confirm',
            'Do you want to create the missing outfit {outfitName} for {charName}?'
        ),
        { outfitName, charName }
    );

    const confirmed = await showConfirm(message, {
        yesText: getSettingsModalText('create', 'Create'),
        noText: getSettingsModalText('cancel', 'Cancel'),
        relativeTo: optionBtn,
        zIndex: MISSING_OUTFIT_CONFIRM_Z_INDEX
    });

    if (!confirmed || !charId || outfitId === '' || outfitId == null) return;

    const createUrl = `/create-video.html?mode=sprite-text&char=${encodeURIComponent(charId)}&outfit=${encodeURIComponent(outfitId)}`;
    if (window.router?.navigate) {
        window.router.navigate(createUrl);
        return;
    }
    window.location.href = createUrl;
};

async function refreshOutfitDropdowns() {
    try {
        const hangoutConfig = await storageManager.getItem('currentHangoutConfig') || {};

        if (!hangoutConfig?.allCharacters) return;

        const hangoutState = HangoutState.getInstance();
        const allCharacters = hangoutState.getCharacters();
        const lang = resolveSettingsLanguage();
        const fallbackScenarioId = hangoutState.getCurrentSession()?.scenarioId || null;
        const scenarioId = hangoutConfig?.scenarioId || hangoutConfig?.selectedScenario?.id || fallbackScenarioId;
        const exceptionKeys = await loadOutfitExceptionKeys();

        for (let index = 0; index < Math.min(hangoutConfig.allCharacters.length, 3); index++) {
            const char = hangoutConfig.allCharacters[index];
            const charSelectEl = document.getElementById(`crew-char-${index + 1}-settings`);
            const roleSelectEl = document.getElementById(`crew-role-${index + 1}-settings`);
            const previewId = CREW_MAP[index]?.preview;

            if (charSelectEl?.value && roleSelectEl) {
                const selectedCharacter = await getEnhancedCharacterData(char.id, allCharacters);
                const characterGender = normalizeGender(selectedCharacter);
                const { outfits: characterOutfits } = await loadOutfits(lang, characterGender);
                const { membershipTier } = await getUserData();
                const characterId = charSelectEl.value || char?.id || selectedCharacter?.id || null;
                const characterOutfitIds = await fetchCharacterOutfitIds(characterId);

                const currentValue = roleSelectEl.value;
                const selectOutfitText = getSettingsModalText('select_outfit', 'Select an outfit');
                roleSelectEl.innerHTML = `<option value="">${selectOutfitText}</option>`;

                appendOutfitSelectOptions(roleSelectEl, characterOutfits, {
                    membershipTier,
                    scenarioId,
                    characterId,
                    exceptionKeys,
                    characterOutfitIds
                });

                if (currentValue && roleSelectEl.querySelector(`option[value="${currentValue}"]`)) {
                    roleSelectEl.value = currentValue;
                } else {
                    roleSelectEl.value = '';
                    if (previewId) setPreviewLocked(previewId, false);
                }
            }
        }
    } catch (error) {
        console.error('Settings Modal: Error refreshing outfit dropdowns:', error);
    }
}

const hydrateSettingsModalSelections = async () => {
    try {
        const configuredCharacters = await getConfiguredActiveCharacters();
        const crewMap = CREW_MAP;
        const desiredCount = Math.min(Math.max(configuredCharacters.length || 1, 1), crewMap.length);
        const numCharactersSelect = document.getElementById('num-allCharacters-settings');

        if (numCharactersSelect) {
            const previousValue = numCharactersSelect.value;
            const desiredValue = toIdString(desiredCount);
            numCharactersSelect.value = desiredValue;
            if (previousValue !== desiredValue) {
                numCharactersSelect.dispatchEvent(new Event('change'));
            } else {
                numCharactersSelect.dispatchEvent(new Event('change'));
            }
        }

        await Promise.all(crewMap.map(async ({ charSelect, roleSelect }, index) => {
            const charSelectEl = document.getElementById(charSelect);
            if (!charSelectEl) return;

            const roleSelectEl = document.getElementById(roleSelect);
            const configCharacter = configuredCharacters[index];
            const characterIdValue = configCharacter ? toIdString(configCharacter.id) : '';

            if (charSelectEl.value !== characterIdValue) {
                charSelectEl.value = characterIdValue;
            }

            const preferredOutfitId = configCharacter ? toIdString(getOutfitId(configCharacter)) : '';

            if (roleSelectEl) {
                roleSelectEl.dataset.preferredOutfitId = preferredOutfitId;
                if (!configCharacter) {
                    roleSelectEl.value = '';
                }
            }

            charSelectEl.dispatchEvent(new Event('change'));
        }));

        const { backgroundId, timeValue } = await resolveSettingsLocationAndTime();
        applySettingsLocationAndTimeToSelects(
            document.getElementById('generate-bg-settings'),
            document.getElementById('time-of-day-settings'),
            { backgroundId, timeValue }
        );
        await refreshSettingsBackgroundPreview();

    } catch (error) {
        console.error('Settings Modal: Failed to hydrate configured selections:', error);
    }
};

export async function createSettingsModal() {
    const hangoutState = HangoutState.getInstance();
    const hangoutService = HangoutService.getInstance();
    const lang = resolveSettingsLanguage();

    const currentState = await getCurrentStateFromMessages();

    const existingModal = document.getElementById('settings-modal');
    if (existingModal) {
        currentSettingsModal = existingModal;
        return existingModal;
    }

    let scenario = hangoutState.getSelectedScenario();
    let scenarioId = null;
    try {
        const hangoutConfig = await storageManager.getItem('currentHangoutConfig') || {};
        scenarioId = hangoutConfig.scenarioId || hangoutConfig.selectedScenario?.id;

        if (!scenarioId) {
            const currentSession = hangoutState.getCurrentSession();
            scenarioId = currentSession?.scenarioId;
        }

        scenarioId = scenarioId || '-1';

        if (scenarioId) {
            scenario = await hangoutService.getScenarioDetails(scenarioId);
        }
    } catch (error) {
        console.warn('Failed to fetch scenario details:', error);
        scenario = scenario || {
            id: scenarioId,
            name: 'Untitled Scenario',
            description: '',
            scenes: [],
            allCharacters: [],
            outfits: []
        };
    }

    // Get character data
    let allCharacters = sortCharactersByRankAsc(await hangoutService.getAvailableCharacters());
    const activeCharacters = currentState.characters;
    const configuredCharactersForTabs = await getConfiguredActiveCharacters();
    let tabCharacters = configuredCharactersForTabs.length > 0 ? configuredCharactersForTabs : (Array.isArray(activeCharacters) ? activeCharacters : []);
    let activeTab = lastSettingsTab || 'scenario';

    const getRosterSignature = (characters) => {
        if (!Array.isArray(characters)) return '';
        return characters
            .map((character) => `${toIdString(character?.id)}:${character?.name || ''}`)
            .join('|');
    };

    const isUtilityTab = (tab) => tab === 'scenario' || tab === 'settings';

    // Preload outfits
    await preloadAllOutfits(lang);
    const { outfits } = await loadOutfits(lang, 'female');

    // Create modal container
    const modal = createElement('div', 'modal create-container', { id: 'settings-modal' });
    modal.dataset.language = toIdString(lang || 'en');

    currentSettingsModal = modal;
    let handleEscape = null;
    let suppressDirty = true;
    let scenarioSnapshot = null;
    let scenarioHydrateToken = 0;
    let actionsBar = null;

    const isScenarioFormDirty = () => (
        Boolean(scenarioSnapshot) && !scenarioSnapshotsEqual(scenarioSnapshot, readScenarioFormSnapshot())
    );

    const syncScenarioActionsVisibility = () => {
        if (!actionsBar) return;
        const shouldShow = activeTab === 'scenario' && isScenarioFormDirty();
        actionsBar.classList.toggle('is-visible', shouldShow);
        actionsBar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    };

    const beginScenarioFormHydrate = () => {
        suppressDirty = true;
        scenarioHydrateToken += 1;
        return scenarioHydrateToken;
    };

    const commitScenarioBaseline = () => {
        scenarioSnapshot = readScenarioFormSnapshot();
        suppressDirty = false;
        syncScenarioActionsVisibility();
    };

    const handleScenarioFormDirty = () => {
        if (suppressDirty) return;
        syncScenarioActionsVisibility();
    };

    (/** @type {any} */ (modal))._beginScenarioFormHydrate = beginScenarioFormHydrate;
    (/** @type {any} */ (modal))._commitScenarioBaseline = commitScenarioBaseline;

    const modalContent = createElement('div', 'modal-content create-panel');

    const tabsMount = createElement('div', 'settings-tabs-wrapper');

    const viewsWrapper = createElement('div', 'settings-unified-views');

    const scenarioView = createElement('div', 'settings-view-container');
    const preferencesView = createElement('div', 'settings-preferences-view is-hidden');
    const characterView = createElement('div', 'settings-character-view is-hidden');

    const characterInfoShell = createElement('div', 'character-info-container character-info-container--embedded show');
    const characterPanelHost = createElement('div', 'character-info-panel');
    characterInfoShell.appendChild(characterPanelHost);
    characterView.appendChild(characterInfoShell);

    // Modal header
    const modalHeader = createElement('div', 'modal-header panel-header');

    const title = createElement('h2', '', { textContent: 'Scenario Settings' });
    title.setAttribute('data-translate', 'settings_modal.title');
    modalHeader.appendChild(title);

    const closeButton = createElement('button', 'close-settings-modal', { id: 'settings-modal-close-btn', textContent: '×' });
    closeButton.addEventListener('click', () => {
        closeModal('settings-modal');
    });
    modalHeader.appendChild(closeButton);
    modalContent.appendChild(modalHeader);

    // Assigned after createCharacterSection; used when returning to the Scenario tab.
    let applyDynamicSectionTranslations = null;
    let openRouterStatusEl = null;
    let openRouterKeyInput = null;
    let openRouterUseForText = null;
    let openRouterUseForVoice = null;
    let openRouterSaveBtn = null;
    let openRouterKeyConfigured = false;
    let preferencesMounted = false;

    const buildUtilityTab = ({ tabId, iconClass, label, translateKey, onSelect }) => {
        const tabButton = createElement('button', 'character-tab-button settings-utility-tab', { type: 'button' });
        tabButton.setAttribute('data-tab', tabId);
        if (activeTab === tabId) tabButton.classList.add('active');

        const icon = createElement('i', iconClass);
        const textSpan = createElement('span', '', { textContent: label });
        textSpan.setAttribute('data-translate', translateKey);
        tabButton.appendChild(icon);
        tabButton.appendChild(textSpan);

        tabButton.addEventListener('click', () => {
            if (activeTab === tabId) return;
            onSelect();
        });

        return tabButton;
    };

    const hideAllViews = () => {
        scenarioView.classList.add('is-hidden');
        preferencesView.classList.add('is-hidden');
        characterView.classList.add('is-hidden');
    };

    const showScenarioTab = () => {
        activeTab = 'scenario';
        lastSettingsTab = 'scenario';
        hideAllViews();
        scenarioView.classList.remove('is-hidden');
        renderUnifiedTabs();
        if (typeof applyDynamicSectionTranslations === 'function') {
            applyDynamicSectionTranslations(modal);
        }
        syncScenarioActionsVisibility();
    };

    const getOpenRouterAuthHeaders = async () => {
        const authToken = await window.tokenManager?.getValidAccessToken?.() ?? '';
        return {
            'Content-Type': 'application/json',
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
        };
    };

    const formatOpenRouterStatus = (status) => {
        if (!status?.configured) {
            return getSettingsModalText('openrouter_not_configured', 'Not configured');
        }
        const connected = getSettingsModalText('openrouter_connected', 'Connected ({hint})');
        return connected.replace('{hint}', status.key_hint || '…');
    };

    const setOpenRouterStatus = ({ state = 'idle', text = '' } = {}) => {
        if (!openRouterStatusEl) return;
        openRouterStatusEl.replaceChildren();

        const dot = createElement('span', 'openrouter-status-dot');
        dot.setAttribute('data-state', state);
        dot.setAttribute('aria-hidden', 'true');

        const label = createElement('span', 'openrouter-status-text', { textContent: text });
        openRouterStatusEl.appendChild(dot);
        openRouterStatusEl.appendChild(label);
    };

    const syncOpenRouterInputLock = (isConfigured) => {
        openRouterKeyConfigured = Boolean(isConfigured);
        if (openRouterKeyInput) {
            openRouterKeyInput.disabled = openRouterKeyConfigured;
            if (openRouterKeyConfigured) openRouterKeyInput.value = '';
        }
        if (openRouterSaveBtn) openRouterSaveBtn.disabled = openRouterKeyConfigured;
    };

    const applyOpenRouterUsageFlags = (status) => {
        const configured = Boolean(status?.configured);
        if (openRouterUseForText) {
            openRouterUseForText.checked = configured ? status?.use_for_text !== false : true;
        }
        if (openRouterUseForVoice) {
            openRouterUseForVoice.checked = configured ? status?.use_for_voice !== false : true;
        }
    };

    const applyOpenRouterStatusFromResponse = (status) => {
        const isConfigured = Boolean(status?.configured);
        setOpenRouterStatus({
            state: isConfigured ? 'ok' : 'idle',
            text: formatOpenRouterStatus(status)
        });
        syncOpenRouterInputLock(isConfigured);
        applyOpenRouterUsageFlags(status);
    };

    const refreshOpenRouterStatus = async () => {
        if (!openRouterStatusEl) return;
        setOpenRouterStatus({
            state: 'loading',
            text: getSettingsModalText('openrouter_not_configured', 'Not configured')
        });
        try {
            const authToken = await window.tokenManager?.getValidAccessToken?.() ?? '';
            if (!authToken) {
                setOpenRouterStatus({
                    state: 'error',
                    text: getSettingsModalText('openrouter_sign_in', 'Sign in to manage your OpenRouter key')
                });
                syncOpenRouterInputLock(false);
                return;
            }
            const status = await fetchOpenRouterKeyStatus({ method: 'GET' });
            applyOpenRouterStatusFromResponse(status);
        } catch (error) {
            console.warn('Settings modal: Failed to load OpenRouter key status', error);
            setOpenRouterStatus({
                state: 'error',
                text: getSettingsModalText('openrouter_status_error', 'Unable to load key status')
            });
        }
    };

    let refreshChatMemoryUi = async () => {};

    const ensurePreferencesContent = async () => {
        if (preferencesMounted) {
            await refreshOpenRouterStatus();
            await refreshChatMemoryUi();
            return;
        }
        preferencesMounted = true;

        const form = createElement('form', 'creation-form character-info-form', { id: 'hangout-preferences-form' });

        const vibeRow = createElement('div', 'form-row vibe-row settings-preference-row');
        vibeRow.appendChild(await createVibeSelector());
        form.appendChild(vibeRow);

        const openRouterRow = createElement('div', 'form-row settings-preference-row openrouter-key-row');
        const openRouterGroup = createElement('div', 'form-group');
        const openRouterLabel = createElement('label', '', {
            for: 'openrouter-api-key-input',
            textContent: 'OpenRouter API Key'
        });
        openRouterLabel.setAttribute('data-translate', 'settings_modal.openrouter_key');

        openRouterStatusEl = createElement('div', 'openrouter-key-status');
        setOpenRouterStatus({
            state: 'loading',
            text: getSettingsModalText('openrouter_not_configured', 'Not configured')
        });

        openRouterKeyInput = createElement('input', 'value', {
            id: 'openrouter-api-key-input',
            type: 'password',
            autocomplete: 'off',
            spellcheck: 'false',
            placeholder: 'sk-or-...'
        });
        openRouterKeyInput.setAttribute('data-translate-placeholder', 'settings_modal.openrouter_placeholder');
        openRouterKeyInput.setAttribute('aria-label', 'OpenRouter API key');

        const billingNote = createElement('p', 'openrouter-billing-note', {
            textContent: 'Out of text credits? Use your own key!'
        });
        billingNote.setAttribute('data-translate', 'settings_modal.openrouter_billing_note');

        const guideLink = createElement('a', 'openrouter-billing-note openrouter-guide-link', {
            href: '/guide/byok',
            target: '_blank',
            rel: 'noopener noreferrer',
            textContent: 'How your key works'
        });
        guideLink.setAttribute('data-translate', 'settings_modal.openrouter_guide_link');

        const actions = createElement('div', 'openrouter-key-actions');

        openRouterSaveBtn = createElement('button', 'selection-btn', {
            type: 'button',
            textContent: 'Save Key'
        });
        openRouterSaveBtn.setAttribute('data-translate', 'settings_modal.openrouter_save');
        openRouterSaveBtn.setAttribute('aria-label', 'Save OpenRouter API key');

        const clearBtn = createElement('button', 'selection-btn', {
            type: 'button',
            textContent: 'Clear Key'
        });
        clearBtn.setAttribute('data-translate', 'settings_modal.openrouter_clear');
        clearBtn.setAttribute('aria-label', 'Clear OpenRouter API key');

        const handleSaveOpenRouterKey = async () => {
            if (openRouterKeyConfigured) return;
            const authToken = await window.tokenManager?.getValidAccessToken?.() ?? '';
            if (!authToken) {
                showToast(getSettingsModalText('openrouter_sign_in', 'Sign in to manage your OpenRouter key'), 'exclamation-triangle');
                return;
            }
            const apiKey = openRouterKeyInput?.value?.trim() || '';
            if (!apiKey) {
                showToast(getSettingsModalText('openrouter_required', 'Enter an OpenRouter API key'), 'exclamation-triangle');
                return;
            }
            openRouterSaveBtn.disabled = true;
            setOpenRouterStatus({
                state: 'loading',
                text: getSettingsModalText('openrouter_not_configured', 'Not configured')
            });
            try {
                const status = await fetchOpenRouterKeyStatus({
                    method: 'PUT',
                    body: {
                        apiKey,
                        useForText: Boolean(openRouterUseForText?.checked),
                        useForVoice: Boolean(openRouterUseForVoice?.checked)
                    }
                });
                applyOpenRouterStatusFromResponse(status);
                showToast(getSettingsModalText('openrouter_saved', 'OpenRouter key saved'), 'check');
            } catch (error) {
                console.error('Settings modal: Failed to save OpenRouter key', error);
                setOpenRouterStatus({
                    state: 'error',
                    text: getSettingsModalText('openrouter_save_failed', 'Failed to save OpenRouter key')
                });
                syncOpenRouterInputLock(false);
                showToast(getSettingsModalText('openrouter_save_failed', 'Failed to save OpenRouter key'), 'exclamation-triangle');
            }
        };

        const handleClearOpenRouterKey = async () => {
            const authToken = await window.tokenManager?.getValidAccessToken?.() ?? '';
            if (!authToken) {
                showToast(getSettingsModalText('openrouter_sign_in', 'Sign in to manage your OpenRouter key'), 'exclamation-triangle');
                return;
            }
            clearBtn.disabled = true;
            setOpenRouterStatus({
                state: 'loading',
                text: getSettingsModalText('openrouter_not_configured', 'Not configured')
            });
            try {
                const status = await fetchOpenRouterKeyStatus({ method: 'DELETE' });
                applyOpenRouterStatusFromResponse(status);
                showToast(getSettingsModalText('openrouter_cleared', 'OpenRouter key cleared'), 'check');
            } catch (error) {
                console.error('Settings modal: Failed to clear OpenRouter key', error);
                setOpenRouterStatus({
                    state: 'error',
                    text: getSettingsModalText('openrouter_clear_failed', 'Failed to clear OpenRouter key')
                });
                showToast(getSettingsModalText('openrouter_clear_failed', 'Failed to clear OpenRouter key'), 'exclamation-triangle');
            } finally {
                clearBtn.disabled = false;
            }
        };

        openRouterSaveBtn.addEventListener('click', (event) => {
            event.preventDefault();
            handleSaveOpenRouterKey();
        });
        clearBtn.addEventListener('click', (event) => {
            event.preventDefault();
            handleClearOpenRouterKey();
        });

        const usageFlags = createElement('div', 'openrouter-usage-flags');
        const createUsageFlag = (id, translateKey, labelText) => {
            const label = createElement('label', 'openrouter-usage-flag', { for: id });
            const input = createElement('input', '', { id, type: 'checkbox' });
            input.checked = true;
            input.setAttribute('aria-label', labelText);
            const caption = createElement('span', '', { textContent: labelText });
            caption.setAttribute('data-translate', translateKey);
            label.appendChild(input);
            label.appendChild(caption);
            return { label, input };
        };
        const textFlag = createUsageFlag(
            'openrouter-use-for-text',
            'settings_modal.openrouter_use_for_text',
            'Use for text'
        );
        const voiceFlag = createUsageFlag(
            'openrouter-use-for-voice',
            'settings_modal.openrouter_use_for_voice',
            'Use for voice'
        );
        openRouterUseForText = textFlag.input;
        openRouterUseForVoice = voiceFlag.input;

        const handleUsageFlagChange = async () => {
            if (!openRouterKeyConfigured) return;
            try {
                const status = await fetchOpenRouterKeyStatus({
                    method: 'PATCH',
                    body: {
                        useForText: Boolean(openRouterUseForText?.checked),
                        useForVoice: Boolean(openRouterUseForVoice?.checked)
                    }
                });
                applyOpenRouterStatusFromResponse(status);
            } catch (error) {
                console.error('Settings modal: Failed to update OpenRouter key usage', error);
                showToast(getSettingsModalText('openrouter_save_failed', 'Failed to save OpenRouter key'), 'exclamation-triangle');
                await refreshOpenRouterStatus();
            }
        };
        openRouterUseForText.addEventListener('change', handleUsageFlagChange);
        openRouterUseForVoice.addEventListener('change', handleUsageFlagChange);

        usageFlags.appendChild(textFlag.label);
        usageFlags.appendChild(voiceFlag.label);
        actions.appendChild(openRouterSaveBtn);
        actions.appendChild(clearBtn);
        openRouterGroup.appendChild(openRouterLabel);
        openRouterGroup.appendChild(openRouterStatusEl);
        openRouterGroup.appendChild(openRouterKeyInput);
        openRouterGroup.appendChild(billingNote);
        openRouterGroup.appendChild(guideLink);
        openRouterGroup.appendChild(usageFlags);
        openRouterGroup.appendChild(actions);
        openRouterRow.appendChild(openRouterGroup);
        form.appendChild(openRouterRow);

        const memoryRow = createElement('div', 'form-row settings-preference-row chat-memory-row');
        const memoryGroup = createElement('div', 'form-group');
        const memoryLabel = createElement('label', '', {
            for: 'hangout-chat-summary',
            textContent: 'Chat summary'
        });
        memoryLabel.setAttribute('data-translate', 'settings_modal.chat_summary');

        const memoryHelp = createElement('p', 'chat-memory-help', {
            textContent: 'Condensed memory of what happened in this hangout. Characters already have their profile; this is so they remember the chat.'
        });
        memoryHelp.setAttribute('data-translate', 'settings_modal.chat_summary_help');

        const pendingEl = createElement('p', 'chat-memory-pending');

        const memoryTextarea = createElement('textarea', 'value', {
            id: 'hangout-chat-summary',
            rows: '6'
        });
        memoryTextarea.maxLength = CHAT_SUMMARY_MAX_LENGTH;
        memoryTextarea.setAttribute('aria-label', 'Chat summary');
        memoryTextarea.setAttribute('data-translate-placeholder', 'settings_modal.chat_summary_placeholder');
        memoryTextarea.placeholder = 'Write a short recap of what has happened so far.';

        const memoryTokenEl = createElement('div', 'chat-memory-token-count');
        const autoSummaryBtn = createElement('button', 'button selection-btn', {
            type: 'button',
            textContent: 'Auto summary'
        });
        autoSummaryBtn.setAttribute('data-translate', 'settings_modal.chat_summary_auto');
        autoSummaryBtn.setAttribute('aria-label', 'Auto summary');

        const updatePendingLine = () => {
            const pending = getPendingSummaryStats();
            pendingEl.textContent = interpolate(
                getSettingsModalText(
                    'chat_summary_pending',
                    'You have ~{n} messages ({t} tokens) left to summarize.'
                ),
                { n: pending.count, t: pending.tokens }
            );
        };

        const updateMemoryTokenCount = () => {
            memoryTokenEl.textContent = formatTokenCount(estimateTokens(memoryTextarea.value));
        };

        refreshChatMemoryUi = async () => {
            const memory = getChatMemory();
            if (document.activeElement !== memoryTextarea) {
                memoryTextarea.value = memory.text;
            }
            updateMemoryTokenCount();
            updatePendingLine();
        };

        memoryTextarea.addEventListener('input', () => {
            const text = memoryTextarea.value.slice(0, CHAT_SUMMARY_MAX_LENGTH);
            memoryTextarea.value = text;
            const current = getChatMemory();
            setChatMemory({
                text,
                summarizedCount: text.trim() ? current.summarizedCount : 0
            });
            updateMemoryTokenCount();
            updatePendingLine();
            schedulePersistChatSummary();
        });

        autoSummaryBtn.addEventListener('click', async (event) => {
            event.preventDefault();
            if (autoSummaryBtn.disabled) return;
            autoSummaryBtn.disabled = true;
            const authToken = await window.tokenManager?.getValidAccessToken?.() ?? '';
            if (!authToken) {
                autoSummaryBtn.disabled = false;
                showToast(getSettingsModalText('chat_summary_sign_in', 'Sign in to generate an auto summary'), 'exclamation-triangle');
                return;
            }
            const pending = getPendingSummaryStats();
            if (pending.count === 0) {
                autoSummaryBtn.disabled = false;
                showToast(getSettingsModalText('chat_summary_auto_empty', 'No new messages to summarize'), 'exclamation-triangle');
                return;
            }
            try {
                const existingSummary = getChatMemory().text;
                const result = await fetchCache.fetch(
                    `${GROK_WORKER_URL.replace(/\/+$/, '')}/v1/hangout/summarize`,
                    {
                        method: 'POST',
                        headers: await getOpenRouterAuthHeaders(),
                        body: JSON.stringify({
                            existingSummary,
                            messages: pending.messages.map((msg) => ({
                                role: msg.role,
                                content: String(msg.content || '').slice(0, 500),
                                ...(msg.characterName ? { characterName: String(msg.characterName) } : {})
                            }))
                        }),
                        cache: 'no-store'
                    }
                );
                const summary = typeof result?.summary === 'string' ? result.summary.trim() : '';
                if (!summary) {
                    showToast(getSettingsModalText('chat_summary_auto_failed', 'Failed to generate summary'), 'exclamation-triangle');
                    return;
                }
                memoryTextarea.value = summary.slice(0, CHAT_SUMMARY_MAX_LENGTH);
                setChatMemory({
                    text: memoryTextarea.value,
                    summarizedCount: getSummarizableHistory().length
                });
                updateMemoryTokenCount();
                updatePendingLine();
                schedulePersistChatSummary();
            } catch (error) {
                console.error('Settings modal: Failed to auto-summarize hangout', error);
                showToast(getSettingsModalText('chat_summary_auto_failed', 'Failed to generate summary'), 'exclamation-triangle');
            } finally {
                autoSummaryBtn.disabled = false;
            }
        });

        memoryGroup.appendChild(memoryLabel);
        memoryGroup.appendChild(memoryHelp);
        memoryGroup.appendChild(pendingEl);
        memoryGroup.appendChild(memoryTextarea);
        memoryGroup.appendChild(memoryTokenEl);
        memoryGroup.appendChild(autoSummaryBtn);
        memoryRow.appendChild(memoryGroup);
        form.appendChild(memoryRow);
        await refreshChatMemoryUi();

        preferencesView.appendChild(form);
        applyTranslationsToContainer(preferencesView, 'hangout', resolveSettingsLanguage());
        await refreshOpenRouterStatus();
    };

    const showPreferencesTab = async () => {
        activeTab = 'settings';
        lastSettingsTab = 'settings';
        hideAllViews();
        preferencesView.classList.remove('is-hidden');
        renderUnifiedTabs();
        await ensurePreferencesContent();
        syncScenarioActionsVisibility();
    };

    const showCharacterTab = async (characterId) => {
        const targetId = characterId ? characterId.toString() : '';
        if (!targetId) return;

        const existingGrid = characterPanelHost.querySelector('.form-group[data-character-id]');
        const canReuseGrid = existingGrid?.dataset.characterId === targetId;

        activeTab = targetId;
        lastSettingsTab = targetId;
        hideAllViews();
        characterView.classList.remove('is-hidden');

        try {
            if (canReuseGrid) {
                await refreshSpriteGridForCurrentOutfit(characterPanelHost);
            } else {
                characterPanelHost.innerHTML = '';
                await renderCharacterInfoContent(characterPanelHost, targetId, { showTabs: false, showClose: false });
            }
        } catch (error) {
            console.error('Settings modal: Failed to render character info content:', error);
            characterPanelHost.textContent = 'Failed to load character info';
        }
        renderUnifiedTabs();
        syncScenarioActionsVisibility();
    };

    const renderUnifiedTabs = () => {
        tabsMount.innerHTML = '';
        const tabsContainer = createCharacterTabs(
            tabCharacters,
            { id: activeTab },
            {
                includeSingle: true,
                activeCharacterId: isUtilityTab(activeTab) ? null : activeTab,
                onSelect: async (characterId) => {
                    if (!characterId) return;
                    await showCharacterTab(characterId);
                }
            }
        ) || createElement('div', 'character-tabs-container');

        if (!tabsContainer.classList.contains('character-tabs-container')) {
            tabsContainer.className = 'character-tabs-container';
        }

        const scenarioTab = buildUtilityTab({
            tabId: 'scenario',
            iconClass: 'fas fa-scroll',
            label: 'Scenario',
            translateKey: 'settings_modal.tab_scenario',
            onSelect: showScenarioTab
        });
        const settingsTab = buildUtilityTab({
            tabId: 'settings',
            iconClass: 'fas fa-cog',
            label: 'Settings',
            translateKey: 'settings_modal.tab_settings',
            onSelect: () => { showPreferencesTab(); }
        });
        tabsContainer.insertBefore(settingsTab, tabsContainer.firstChild);
        tabsContainer.insertBefore(scenarioTab, tabsContainer.firstChild);

        tabsMount.appendChild(tabsContainer);
    };

    const syncCharacterTabsFromConfig = async () => {
        const configuredCharacters = await getConfiguredActiveCharacters();
        const nextTabCharacters = configuredCharacters.length > 0
            ? configuredCharacters
            : (Array.isArray(activeCharacters) ? activeCharacters : []);

        if (getRosterSignature(tabCharacters) === getRosterSignature(nextTabCharacters)) {
            return false;
        }

        tabCharacters = nextTabCharacters;
        const nextIds = new Set(
            tabCharacters.map((character) => toIdString(character?.id)).filter(Boolean)
        );

        if (!isUtilityTab(activeTab) && !nextIds.has(toIdString(activeTab))) {
            characterPanelHost.innerHTML = '';
            showScenarioTab();
            return true;
        }

        if (!isUtilityTab(activeTab)) {
            characterPanelHost.innerHTML = '';
            try {
                await renderCharacterInfoContent(characterPanelHost, activeTab, {
                    showTabs: false,
                    showClose: false
                });
            } catch (error) {
                console.error('Settings modal: Failed to refresh character info content:', error);
                characterPanelHost.textContent = 'Failed to load character info';
            }
        }

        renderUnifiedTabs();
        return true;
    };

    modalContent.appendChild(tabsMount);
    modalContent.appendChild(viewsWrapper);

    // Form setup
    const form = createElement('form', 'creation-form', { id: 'scenario-settings-form' });

    const titleSection = createElement('div', 'form-group');

    const scenarioGroup = createElement('div', 'form-group');
    const scenarioLabel = createElement('label', '', {
        for: 'scenario-description-settings',
        textContent: 'Scenario Description'
    });
    scenarioLabel.setAttribute('data-translate', 'settings_modal.scenario_description');

    const scenarioTextarea = createElement('textarea', 'settings-scenario-textarea', {
        id: 'scenario-description-settings',
        name: 'scenario-description',
        required: true
    });
    scenarioTextarea.placeholder = "Describe your scenario (e.g., \"You're a rogue chasing a cursed veil with a flirty party in a mystical tavern!\")";
    scenarioTextarea.setAttribute('data-translate-placeholder', 'settings_modal.scenario_placeholder');
    scenarioTextarea.value = resolveScenarioDescriptionForModal({
        currentState,
        hangoutState,
        scenario
    });

    const scenarioTokenEl = createElement('div', 'scenario-description-token-count');
    const updateScenarioTokenCount = () => {
        scenarioTokenEl.textContent = formatTokenCount(estimateTokens(scenarioTextarea.value));
    };
    scenarioTextarea.addEventListener('input', updateScenarioTokenCount);
    updateScenarioTokenCount();

    scenarioGroup.appendChild(scenarioLabel);
    scenarioGroup.appendChild(scenarioTextarea);
    scenarioGroup.appendChild(scenarioTokenEl);
    titleSection.appendChild(scenarioGroup);
    form.appendChild(titleSection);

    // Character crew section
    const characterSection = createCharacterSection(allCharacters, activeCharacters);
    const { crewSection, numCharactersSelect, crewMap } = characterSection;
    applyDynamicSectionTranslations = characterSection.applyDynamicSectionTranslations;
    const previewStateManager = new PreviewStateManager(crewMap);

    form.appendChild(crewSection);

    // Background section
    const {
        backgroundSection,
        backgroundSelect,
        timeOfDaySelect,
        backgroundPreview,
        bgLeftArrow,
        bgRightArrow
    } = createBackgroundSection(currentState);
    form.appendChild(backgroundSection);

    // Load background options
    await loadBackgroundOptions(
        backgroundSelect,
        timeOfDaySelect,
        backgroundPreview,
        bgLeftArrow,
        bgRightArrow
    );

    // Load current configuration
    const loadCurrentConfiguration = async () => {
        try {
            const hangoutState = HangoutState.getInstance();
            const currentState = await getCurrentStateFromMessages();

            const descriptionTextarea = modal.querySelector('#scenario-description-settings');
            if (descriptionTextarea) {
                descriptionTextarea.value = resolveScenarioDescriptionForModal({
                    currentState,
                    hangoutState,
                    scenario
                });
            }

            for (let index = 0; index < currentState.characters.length && index < crewMap.length; index++) {
                const char = currentState.characters[index];
                const { charSelect, roleSelect } = crewMap[index];
                const characterSelect = modal.querySelector(`#${charSelect}`);
                const roleSelectEl = modal.querySelector(`#${roleSelect}`);
                const goalTextarea = modal.querySelector(`#char-${index + 1}-goal-settings`);

                if (characterSelect && char.id) {
                    characterSelect.value = char.id;

                    if (roleSelectEl) {
                        roleSelectEl.dataset.preferredOutfitId = toIdString(char.outfitId || 0);
                    }

                    if (goalTextarea) {
                        const goalFromChar = typeof char.goal === 'string' ? char.goal.trim() : '';
                        const goalFromState = toIdString(
                            currentState.goals?.[char.id] ?? currentState.goals?.[toIdString(char.id)] ?? ''
                        ).trim();
                        const scenarioGoal = getScenarioGoalForSlot(scenario, index, char.id);
                        goalTextarea.value = scenarioGoal || goalFromChar || goalFromState || '';
                    }
                }
            }

        } catch (error) {
            console.error('Error loading current configuration:', error);
        }
    };

    await loadCurrentConfiguration();

    // Validation Message Container
    const validationSection = createElement('div', 'form-group');
    const validationMessage = createElement('div', 'settings-validation-message is-hidden', { id: 'outfit-validation-message' });
    validationSection.appendChild(validationMessage);

    actionsBar = createElement('div', 'scenario-settings-actions sprite-gallery-actions');
    actionsBar.setAttribute('role', 'group');
    actionsBar.setAttribute('aria-hidden', 'true');

    const revertButton = createElement('button', 'sprite-gallery-btn sprite-gallery-btn-secondary', {
        type: 'button',
        textContent: 'Revert changes'
    });
    revertButton.setAttribute('data-translate', 'settings_modal.revert_changes');
    revertButton.setAttribute('aria-label', 'Revert changes');

    const submitButton = createElement('button', 'sprite-gallery-btn sprite-gallery-btn-primary', {
        type: 'submit',
        textContent: 'Save Changes'
    });
    submitButton.setAttribute('form', 'scenario-settings-form');
    submitButton.setAttribute('data-translate', 'settings_modal.save_changes');
    submitButton.setAttribute('aria-label', 'Save Changes');

    revertButton.addEventListener('click', async () => {
        if (!scenarioSnapshot) return;
        beginScenarioFormHydrate();
        try {
            await applyScenarioSnapshotValues(scenarioSnapshot);
        } finally {
            suppressDirty = false;
            syncScenarioActionsVisibility();
        }
    });

    let saveInFlight = false;
    const handleSaveScenarioSettings = async (event) => {
        event.preventDefault();
        if (saveInFlight) return;
        saveInFlight = true;
        try {
            const saved = await handleSettingsSubmit({
                crewMap,
                allCharacters,
                scenario,
                storageManager,
                validationMessage,
                handleEscape,
                setHandleEscape: (val) => { handleEscape = val; }
            });
            if (saved) commitScenarioBaseline();
        } finally {
            saveInFlight = false;
        }
    };

    submitButton.addEventListener('click', handleSaveScenarioSettings);
    form.addEventListener('submit', handleSaveScenarioSettings);

    form.addEventListener('input', handleScenarioFormDirty);
    form.addEventListener('change', handleScenarioFormDirty);

    form.appendChild(validationSection);
    actionsBar.appendChild(revertButton);
    actionsBar.appendChild(submitButton);
    scenarioView.appendChild(form);
    viewsWrapper.appendChild(scenarioView);
    viewsWrapper.appendChild(preferencesView);
    viewsWrapper.appendChild(characterView);
    modalContent.appendChild(actionsBar);

    const restoreLastTab = async () => {
        if (lastSettingsTab === 'settings') {
            await showPreferencesTab();
            return;
        }
        if (lastSettingsTab && lastSettingsTab !== 'scenario') {
            await showCharacterTab(lastSettingsTab);
            if (activeTab === lastSettingsTab) return;
        }
        showScenarioTab();
    };

    await restoreLastTab();
    (/** @type {any} */ (modal))._restoreLastTab = restoreLastTab;
    (/** @type {any} */ (modal))._showScenarioTab = showScenarioTab;
    (/** @type {any} */ (modal))._showSettingsTab = showPreferencesTab;
    (/** @type {any} */ (modal))._syncCharacterTabsFromConfig = syncCharacterTabsFromConfig;

    modal.appendChild(modalContent);
    
    // Event handlers for preview arrows
    modal.addEventListener('settingsPreviewArrow', (event) => {
        const detail = (/** @type {any} */ (event)).detail || {};
        if (typeof detail.slotIndex === 'number' && typeof detail.direction === 'number') {
            previewStateManager.handleArrowClick(detail.slotIndex, detail.direction);
        }
    });

    document.body.appendChild(modal);
    await loadTranslations('hangout', resolveSettingsLanguage());
    applyTranslationsToContainer(modal, 'hangout', resolveSettingsLanguage());
    if (typeof applyDynamicSectionTranslations === 'function') {
        applyDynamicSectionTranslations(modal);
    }

    // Initialize Create-style searchable dropdowns (same as Create Video page)
    const initSettingsSearchDropdowns = async () => {
        const selectCharacterText = getSettingsModalText('select_character', 'Select a character');
        const selectOutfitText = getSettingsModalText('select_outfit', 'Select an outfit');
        const selectBackgroundText = getSettingsModalText('select_background', 'Select a background');
        const numberOfCharactersText = getSettingsModalText('number_of_characters', 'Number of Characters');
        const timeOfDayText = getSettingsModalText('time_day', 'Day');

        initSearchDropdown('num-allCharacters-settings', {
            placeholder: numberOfCharactersText || 'Number of Characters',
            ariaLabel: numberOfCharactersText || 'Number of Characters',
            hideSearch: true
        });

        // Crew: characters + outfits
        crewMap.forEach(({ charSelect, roleSelect }, slotIndex) => {
            initSearchDropdown(charSelect, {
                placeholder: selectCharacterText || 'Select a character',
                searchLabel: selectCharacterText || 'Select a character',
                searchPlaceholder: selectCharacterText || 'Select a character',
                ariaLabel: selectCharacterText || 'Select a character'
            });

            // Replace the character dropdown interaction with a ranked picker modal.
            // This keeps the underlying <select> as the source of truth so existing change handlers keep working.
            attachRankedCharacterPickerToTrigger({
                selectId: charSelect,
                allCharacters,
                crewMap,
                slotIndex
            });

            initSearchDropdown(roleSelect, {
                placeholder: selectOutfitText || 'Select an outfit',
                searchLabel: selectOutfitText || 'Select an outfit',
                searchPlaceholder: selectOutfitText || 'Select an outfit',
                ariaLabel: selectOutfitText || 'Select an outfit',
                onUnavailableClick: (option, optionBtn) => {
                    handleMissingOutfitOption(option, optionBtn, slotIndex, crewMap);
                }
            });
        });

        // Background + time
        initSearchDropdown('generate-bg-settings', {
            placeholder: selectBackgroundText || 'Select a background',
            searchLabel: selectBackgroundText || 'Select a background',
            searchPlaceholder: selectBackgroundText || 'Select a background',
            ariaLabel: selectBackgroundText || 'Select a background'
        });
        // Replace background dropdown interaction with a location picker modal (card grid with background thumbnails).
        attachCreateLocationPickerToTrigger(/** @type {any} */ ({
            selectId: 'generate-bg-settings',
            timeSelectId: 'time-of-day-settings'
        }));

        initSearchDropdown('time-of-day-settings', {
            placeholder: timeOfDayText || 'Day',
            ariaLabel: timeOfDayText || 'Time of day',
            hideSearch: true
        });
    };

    try {
        await initSettingsSearchDropdowns();
    } catch (error) {
        console.warn('Settings modal: Failed to init search dropdowns:', error);
    }

    // Subscriptions
    const sessionSubscription = hangoutState.subscribe('SESSION_STARTED', async () => {
        if (currentSettingsModal && currentSettingsModal.classList.contains('is-open')) {
            setTimeout(async () => {
                await refreshSettingsModalContent();
            }, 200);
        }
    });

    const cleanupSubscriptions = () => {
        if (sessionSubscription) hangoutState.unsubscribe('SESSION_STARTED', sessionSubscription);
    };

    modal.onclick = async (e) => {
        if (e.target === modal) {
            cleanupSubscriptions();
            if (handleEscape) {
                document.removeEventListener('keydown', handleEscape);
                handleEscape = null;
            }
            closeModal('settings-modal');
        }
    };

    handleEscape = async (event) => {
        const key = event.key || event.code;
        if (key === 'Escape' || key === 'Esc') {
            event.preventDefault();
            if (modal.classList.contains('show') === false) return;
            cleanupSubscriptions();
            document.removeEventListener('keydown', handleEscape);
            handleEscape = null;
            closeModal('settings-modal');
        }
    };
    document.addEventListener('keydown', handleEscape);

    // Visibility and character change handlers
    let lastVisibleCharacterCount = (() => {
        const parsed = parseInt(numCharactersSelect.value, 10);
        if (!Number.isFinite(parsed)) return 1;
        return Math.min(Math.max(parsed, 1), crewMap.length);
    })();

    const autoSelectFirstAvailableCharacters = (fromIndex, toCount) => {
        const targetCount = Math.min(Math.max(toCount, 1), crewMap.length);
        const startIndex = Math.min(Math.max(fromIndex, 0), crewMap.length);
        if (startIndex >= targetCount) return;

        const getUsedCharacterIds = (excludeIndex) => {
            const used = new Set();
            for (let i = 0; i < targetCount; i++) {
                if (i === excludeIndex) continue;
                const otherSelectId = crewMap[i]?.charSelect;
                const otherSelectEl = otherSelectId ? document.getElementById(otherSelectId) : null;
                const value = otherSelectEl?.value ? toIdString(otherSelectEl.value) : '';
                if (value) used.add(value);
            }
            return used;
        };

        for (let slotIndex = startIndex; slotIndex < targetCount; slotIndex++) {
            const charSelectId = crewMap[slotIndex]?.charSelect;
            const charSelectEl = charSelectId ? document.getElementById(charSelectId) : null;
            if (!charSelectEl) continue;

            // Only auto-select if the slot is currently empty.
            if (charSelectEl.value) continue;

            const usedIds = getUsedCharacterIds(slotIndex);
            const candidate = Array.from(charSelectEl.options).find(opt => {
                const id = opt?.value ? toIdString(opt.value) : '';
                if (!id) return false; // skip placeholder
                if (opt.disabled) return false;
                if (usedIds.has(id)) return false;
                return true;
            });

            if (!candidate) continue;

            charSelectEl.value = toIdString(candidate.value);
            charSelectEl.dispatchEvent(new Event('change'));
        }
    };

    numCharactersSelect.addEventListener('change', async () => {
        const newValueRaw = parseInt(numCharactersSelect.value, 10);
        const newValue = Number.isFinite(newValueRaw) ? Math.min(Math.max(newValueRaw, 1), crewMap.length) : 1;
        const previousValue = lastVisibleCharacterCount;
        lastVisibleCharacterCount = newValue;

        updateCharacterSectionsVisibility(crewMap, newValue, updatePreview);
        autoSelectFirstAvailableCharacters(previousValue, newValue);
    });

    setTimeout(() => {
        const parsedCount = Number.parseInt(numCharactersSelect.value, 10);
        const initialNumCharacters = Number.isFinite(parsedCount) && parsedCount > 0
            ? Math.min(parsedCount, crewMap.length)
            : (activeCharacters.length > 0 ? Math.min(activeCharacters.length, 3) : 1);
        if (!numCharactersSelect.value) {
            numCharactersSelect.value = String(initialNumCharacters);
        }
        updateCharacterSectionsVisibility(crewMap, initialNumCharacters, updatePreview);
        lastVisibleCharacterCount = initialNumCharacters;
    }, 0);

    const handleCharacterChange = (dropdown, index) => {
        dropdown.addEventListener('change', async () => {
            const selectedCharId = dropdown.value;
            const charDropdowns = crewMap.map(({ charSelect }) => document.getElementById(charSelect));
            const roleSelect = document.getElementById(crewMap[index].roleSelect);
            const previewId = crewMap[index].preview;

            if (!selectedCharId) {
                updatePreview(previewId, null);
                setPreviewLocked(previewId, false);
                previewStateManager.updateState(index, [], '');
                if (roleSelect) {
                    const selectOutfitText = getSettingsModalText('select_outfit', 'Select an outfit');
                    roleSelect.innerHTML = `<option value="">${selectOutfitText}</option>`;
                    roleSelect.value = '';
                    roleSelect.dataset.preferredOutfitId = '';
                    roleSelect.dataset.characterGender = '';
                }
                if (typeof applyDynamicSectionTranslations === 'function') {
                    applyDynamicSectionTranslations(modal);
                }
                await updateValidationMessage(crewMap);
                return;
            }

            try {
                const selectedCharacter = await getEnhancedCharacterData(selectedCharId, allCharacters);
                const characterGender = normalizeGender(selectedCharacter);
                const { outfits: characterOutfits } = await loadOutfits(lang, characterGender);
                const { membershipTier } = await getUserData();
                const currentConfig = await storageManager.getItem('currentHangoutConfig') || {};
                const scenarioId = currentConfig?.scenarioId || currentConfig?.selectedScenario?.id || hangoutState.getCurrentSession()?.scenarioId || null;
                const exceptionKeys = await loadOutfitExceptionKeys();
                const configuredCharacters = await getConfiguredActiveCharacters();
                const mergedActiveCharacters = configuredCharacters.length > 0 ? configuredCharacters : activeCharacters;
                const slotActiveCharacter = getActiveCharacterForSlot(mergedActiveCharacters, index, selectedCharId);
                let preferredOutfitId = slotActiveCharacter ? toIdString(getOutfitId(slotActiveCharacter)) : '';

                if (!preferredOutfitId && roleSelect?.dataset?.preferredOutfitId) {
                    preferredOutfitId = roleSelect.dataset.preferredOutfitId;
                }

                if (roleSelect) {
                    const selectOutfitText = getSettingsModalText('select_outfit', 'Select an outfit');
                    roleSelect.innerHTML = `<option value="">${selectOutfitText}</option>`;
                    roleSelect.dataset.preferredOutfitId = preferredOutfitId;
                    roleSelect.dataset.characterGender = characterGender;

                    const characterOutfitIds = await fetchCharacterOutfitIds(selectedCharId);
                    appendOutfitSelectOptions(roleSelect, characterOutfits, {
                        membershipTier,
                        scenarioId,
                        characterId: selectedCharId,
                        exceptionKeys,
                        characterOutfitIds
                    });

                    const selectedOutfitId = pickSelectableOutfitId(
                        characterOutfits,
                        preferredOutfitId,
                        roleSelect,
                        {
                            membershipTier,
                            scenarioId,
                            characterId: selectedCharId,
                            exceptionKeys,
                            characterOutfitIds
                        }
                    );

                    roleSelect.value = selectedOutfitId || '';
                    previewStateManager.updateState(index, characterOutfits, roleSelect.value || '');
                    roleSelect.dispatchEvent(new Event('change'));
                } else {
                    previewStateManager.updateState(index, characterOutfits, preferredOutfitId);
                }

                if (typeof applyDynamicSectionTranslations === 'function') {
                    applyDynamicSectionTranslations(modal);
                }

                charDropdowns.forEach((otherDropdown, otherIndex) => {
                    if (otherIndex !== index && otherDropdown) {
                        Array.from(otherDropdown.options).forEach(option => {
                            option.disabled = false;
                            if (option.value && option.value === selectedCharId) {
                                option.disabled = true;
                            } else if (option.value) {
                                option.disabled = charDropdowns.some((dd, i) => i !== otherIndex && dd && dd.value === option.value && dd.value);
                            }
                        });
                    }
                });
            } catch (error) {
                console.error('Failed to handle character change:', error);
            }
        });
    };

    const handleRoleChange = (dropdown, index) => {
        dropdown.addEventListener('change', async () => {
            const charSelect = document.getElementById(crewMap[index].charSelect);
            const previewId = crewMap[index].preview;
            const outfitId = dropdown.value ? toIdString(dropdown.value) : '';
            const selectedOption = dropdown.selectedOptions?.[0] || null;
            const isLockedOutfit = selectedOption?.getAttribute('data-unowned') === 'true';

            dropdown.dataset.preferredOutfitId = outfitId;

            if (!charSelect.value || !outfitId) {
                updatePreview(previewId, null);
                setPreviewLocked(previewId, false);
                previewStateManager.updateState(index, previewStateManager.getState(index)?.outfits || [], outfitId);
                await updateValidationMessage(crewMap);
                return;
            }

            const character = allCharacters.find(char => toIdString(char.id) === toIdString(charSelect.value)) || {};
            console.log(`[settings-modal] handleRoleChange: Character ${charSelect.value}`, {
                id: character.id,
                name: character.name,
            });
            setPreviewLocked(previewId, isLockedOutfit);
            previewStateManager.updateState(index, previewStateManager.getState(index)?.outfits || [], outfitId);
            await fetchSprite(charSelect.value, outfitId, previewId);
            await updateValidationMessage(crewMap);
        });
    };

    crewMap.forEach(({ charSelect, roleSelect }, index) => {
        const charSelectEl = document.getElementById(charSelect);
        const roleSelectEl = document.getElementById(roleSelect);

        if (charSelectEl) handleCharacterChange(charSelectEl, index);
        if (roleSelectEl) handleRoleChange(roleSelectEl, index);
    });

    const scheduleHydration = typeof queueMicrotask === 'function'
        ? queueMicrotask
        : (fn) => Promise.resolve().then(fn);

    const hydrateSelections = async () => {
        const token = beginScenarioFormHydrate();
        try {
            await hydrateSettingsModalSelections();
        } catch (error) {
            console.error('Settings Modal: Failed to hydrate selections after creation:', error);
        } finally {
            if (token === scenarioHydrateToken) {
                commitScenarioBaseline();
            }
        }
    };

    scheduleHydration(hydrateSelections);

    return modal;
}
